import React from 'react';

import { TextButton } from '@components/Button/variants';
import ClientRenderedContent from '@components/ClientRenderedContent';
import useResumeSubscription from '@hooks/useResumeSubscription';
import {
  logout_path,
  manage_app_subscription_path,
  receipts_path,
  resume_subscription_path,
  subscribe_one_path,
  update_payment_path,
  upgrade_subscription_path,
} from '@routes';
import { SettingsResponse } from '@utils/backend-api/settings';

import styles from './styles.module.css';

const LinkSection = ({ userSettings }: { userSettings: SettingsResponse }) => {
  const { mutate: resumeSubscription } = useResumeSubscription();

  const handleResumeSubscription = () => {
    resumeSubscription();
  };

  return (
    <div className={styles.linkContainer}>
      {userSettings.isRevcatSubscriber ? (
        // Revcat users should manager their subscriptions through the app
        <div className={styles.accountManagementLink}>
          This account was created using our mobile app. To manage your account, use your mobile
          device.{' '}
          <a href={manage_app_subscription_path()} id="settings-account-management-more-info-link">
            Click here for more information
          </a>
        </div>
      ) : (
        // Stripe users can manage their subscription here
        <div>
          {userSettings.eligibleToUpgrade && (
            <a href={upgrade_subscription_path()}>Upgrade your subscription {'>'}</a>
          )}
          {userSettings.canResumeSubscription && (
            <div>
              <div id="resume-confirm-container">
                {userSettings.trial?.expiredFromTrial ? (
                  <p>
                    You have canceled your trial. To regain access, resume your subscription today.
                    We won&apos;t bill you until the end of your trial (
                    {userSettings.trial.paidThroughDate} Pacific Time).
                  </p>
                ) : (
                  !userSettings.canResumePausedSubscription && (
                    <p>
                      You will lose access to The Information in{' '}
                      {userSettings.trial?.accessDaysRemaining} days. To ensure that doesn&apos;t
                      happen, resume your subscription today. We won&apos;t bill you until the end
                      of your current plan (
                      <ClientRenderedContent>
                        {userSettings.trial?.dateToCharge}
                      </ClientRenderedContent>{' '}
                      Pacific Time).
                    </p>
                  )
                )}
              </div>
              {userSettings.canResumePausedSubscription ? (
                <TextButton
                  as="button"
                  onClick={handleResumeSubscription}
                  size="small"
                  className="mb-16 px-0 font-suisse-screen no-underline max-sm:w-fit"
                >
                  Resume your subscription {'>'}
                </TextButton>
              ) : (
                <a href={resume_subscription_path()}>Resume your subscription {'>'}</a>
              )}
            </div>
          )}
          {userSettings.canUpdatePayment && (
            <a href={update_payment_path()}>Subscription and billing {'>'}</a>
          )}
          {userSettings.canRestartSubscription && (
            <a href={subscribe_one_path()}>Restart your subscription {'>'}</a>
          )}
          {userSettings.canViewPaymentHistory && (
            <a href={receipts_path()}>View your payment history {'>'}</a>
          )}
        </div>
      )}
      <a href={logout_path()}>Sign out {'>'}</a>
    </div>
  );
};

export default LinkSection;
